import React, { useState, useEffect } from "react";
import { Spinner, Image } from "react-bootstrap";
import { FaTimesCircle } from "react-icons/fa";
import { PublicClientApplication } from "@azure/msal-browser";
import { useNotifications } from "@/hooks/useNotifications";
import { useTimezone } from "@/hooks/useTimezone";
import moment from "moment-timezone";
import { useAuth } from "@/context/auth-context";
import { graphConfig } from "@/config/msal-config";

interface NotificationListProps {
    highlightId?: string | null;
    applicationName: string;
    userEmail: string;
    msalInstance: PublicClientApplication | null;
    onClose?: () => void;
}

const NotificationList: React.FC<NotificationListProps> = ({
    highlightId,
    applicationName,
    userEmail,
    msalInstance,
    onClose,
}) => {
    const { notifications, loading, markAllAsRead, markOneAsRead } =
        useNotifications(applicationName, userEmail, msalInstance);
    const { userProfile } = useAuth();
    const { timeZoneValue } = useTimezone(); // Get user's timezone preference
    const userTimezone = timeZoneValue || "America/New_York"; // Fallback to EST
    const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

    useEffect(() => {
        const fetchAvatar = async () => {
            if (msalInstance && userProfile) {
                try {
                    const account = msalInstance.getActiveAccount();
                    if (account) {
                        const response = await msalInstance.acquireTokenSilent({
                            scopes: ["User.Read"],
                            account: account,
                        });
                        const photoResponse = await fetch(
                            `${graphConfig.graphMeEndpoint}/photo/$value`,
                            {
                                headers: {
                                    Authorization: `Bearer ${response.accessToken}`,
                                },
                            }
                        );
                        if (photoResponse.ok) {
                            const blob = await photoResponse.blob();
                            setAvatarUrl(URL.createObjectURL(blob));
                        } else {
                            setAvatarUrl("/logo.png"); // fallback
                        }
                    }
                } catch (error) {
                    console.error("Failed to fetch avatar:", error);
                    setAvatarUrl("/logo.png"); // fallback
                }
            }
        };

        fetchAvatar();
    }, [msalInstance, userProfile]);

    const formatTimestamp = (dateString: string) => {
        if (!dateString) return "";
        try {
            const date = moment.utc(dateString);
            const userDate = date.clone().tz(userTimezone);

            const now = moment().tz(userTimezone);

            if (userDate.isSame(now, "day")) {
                return `Today at ${userDate.format("h:mm A")}`;
            }

            if (userDate.isSame(now.clone().subtract(1, "days"), "day")) {
                return `Yesterday at ${userDate.format("h:mm A")}`;
            }

            return userDate.format("ddd MMM D h:mm A");
        } catch (e) {
            console.error("Error formatting date:", e);
            return "Invalid Date";
        }
    };

    return (
        <div
            className="notification-list-wrapper"
            style={{
                minWidth: 350,
                maxWidth: 400,
                fontFamily: "'DM Sans', sans-serif",
                display: "flex",
                flexDirection: "column",
                height: "100%",
            }}
        >
            {/* Header with gray background */}
            <div
                className="notification-list-header"
                style={{
                    padding: "14px 16px",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                    }}
                >
                    <h6
                        className="notification-list-title"
                        style={{
                            fontSize: "20px",
                            fontWeight: 600,
                            margin: 0,
                            fontFamily: "'DM Sans', sans-serif",
                        }}
                    >
                        Notifications
                    </h6>
                    {onClose && (
                        <button
                            type="button"
                            className="btn p-0 border-0 bg-transparent text-muted notification-close-btn"
                            onClick={onClose}
                            style={{
                                fontSize: "20px",
                                lineHeight: 1,
                                opacity: 0.6,
                                transition: "opacity 0.2s",
                            }}
                            onMouseEnter={(e) =>
                                (e.currentTarget.style.opacity = "1")
                            }
                            onMouseLeave={(e) =>
                                (e.currentTarget.style.opacity = "0.6")
                            }
                            aria-label="Close"
                        >
                            <FaTimesCircle />
                        </button>
                    )}
                </div>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: "4px",
                    }}
                >
                    <button
                        type="button"
                        className="btn btn-link p-0 text-muted notification-list-action"
                        style={{
                            fontSize: "14px",
                            textDecoration: "none",
                            opacity: 0.7,
                            fontFamily: "'DM Sans', sans-serif",
                        }}
                        onClick={() => markAllAsRead()}
                    >
                        Mark all as read
                    </button>
                </div>
            </div>
            {loading ? (
                <div className="d-flex justify-content-center align-items-center py-4">
                    <Spinner animation="border" size="sm" />
                </div>
            ) : notifications.length === 0 ? (
                <div
                    className="text-center text-muted py-4 notification-list-empty"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                    No notifications for today.
                </div>
            ) : (
                <div
                    className="notification-scroll-area"
                    style={{ maxHeight: 400, overflowY: "auto", flex: 1 }}
                >
                    {notifications.map((n) => (
                        <div
                            key={n.messageId}
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                                if (!n.readIndicator)
                                    markOneAsRead(n.messageId);
                            }}
                            onKeyPress={(e) => {
                                if (
                                    (e.key === "Enter" || e.key === " ") &&
                                    !n.readIndicator
                                ) {
                                    markOneAsRead(n.messageId);
                                }
                            }}
                            style={{
                                cursor: n.readIndicator ? "default" : "pointer",
                            }}
                            className={`d-flex align-items-start p-3 border-bottom bg-${n.readIndicator ? "white" : "light"} ${highlightId === n.messageId ? "notification-highlight" : ""}`}
                        >
                            <Image
                                src={avatarUrl || "/logo.png"}
                                roundedCircle
                                width={36}
                                height={36}
                                alt={userProfile?.displayName || "User"}
                                className="me-2"
                            />
                            <div style={{ flex: 1, position: "relative" }}>
                                <div
                                    className="fw-bold notification-item-title"
                                    style={{
                                        fontSize: 16,
                                        fontFamily: "'DM Sans', sans-serif",
                                    }}
                                >
                                    {n.messageHeader}
                                </div>
                                <div
                                    className="notification-item-body"
                                    style={{
                                        fontSize: 15,
                                        wordBreak: "break-word",
                                        fontFamily: "'DM Sans', sans-serif",
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: n.messageContent,
                                    }}
                                />
                                <div
                                    className="notification-item-timestamp"
                                    style={{
                                        fontSize: 13,
                                        marginTop: "4px",
                                        fontFamily: "'DM Sans', sans-serif",
                                    }}
                                >
                                    {formatTimestamp(n.createdOn)}
                                </div>
                                {!n.readIndicator && (
                                    <span
                                        style={{
                                            position: "absolute",
                                            top: "12px",
                                            right: "12px",
                                            width: "8px",
                                            height: "8px",
                                            borderRadius: "50%",
                                            backgroundColor: "#2ecc40",
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <style jsx global>{`
                .notification-highlight {
                    background: #e6ffe6 !important;
                    animation: notification-fade-highlight 1.5s;
                }
                @keyframes notification-fade-highlight {
                    0% {
                        background: #b6ffb6;
                    }
                    100% {
                        background: #e6ffe6;
                    }
                }

                /* Mobile full-screen */
                @media (max-width: 768px) {
                    .notification-list-wrapper {
                        position: fixed !important;
                        top: 0 !important;
                        left: 0 !important;
                        right: 0 !important;
                        bottom: 0 !important;
                        width: 100vw !important;
                        height: 100vh !important;
                        min-width: 100vw !important;
                        max-width: 100vw !important;
                        background: white !important;
                        z-index: 10000 !important;
                    }

                    .notification-scroll-area {
                        max-height: none !important;
                        flex: 1 !important;
                        overflow-y: auto !important;
                    }

                    .notification-close-btn {
                        opacity: 1 !important;
                        color: #000 !important;
                        font-size: 28px !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default NotificationList;
